import React, { useState, useEffect } from 'react';
import {
  Printer,
  Download,
  Maximize2,
  Check,
  RotateCcw,
  RotateCw,
  Sliders,
  Layers,
  CheckCircle2,
  ArrowLeftRight,
} from 'lucide-react';
import { CompanyInfo, LabelConfig, ServiceTicket } from '../types';
import {
  DEFAULT_LABEL_CONFIG,
  PRESET_DIMENSIONS,
  getStoredLabelConfig,
  saveStoredLabelConfig,
  printCustomLabel,
  printCustomLabelPDF,
  exportCustomLabelPDF,
  calculateLabelMetrics,
  renderLabelToCanvas,
} from '../utils/labelGenerator';
import { formatDateTR } from '../utils/storage';

interface LabelDesignerProps {
  ticket: ServiceTicket;
  companyInfo: CompanyInfo;
  onPrintIntakeMode?: () => void;
}

export const LabelDesigner: React.FC<LabelDesignerProps> = ({
  ticket,
  companyInfo,
}) => {
  const [config, setConfig] = useState<LabelConfig>(getStoredLabelConfig);
  const [activeTab, setActiveTab] = useState<'size' | 'fields'>('size');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // String inputs for smooth editing without jumping
  const [widthInput, setWidthInput] = useState<string>(() => String(config.widthMm));
  const [heightInput, setHeightInput] = useState<string>(() => String(config.heightMm));
  const [copiesInput, setCopiesInput] = useState<string>(() => String(config.copies || 1));

  const [previewDataUrl, setPreviewDataUrl] = useState<string>('');
  const [isRendering, setIsRendering] = useState(true);

  // Sync inputs when config changes
  useEffect(() => {
    setWidthInput(String(config.widthMm));
    setHeightInput(String(config.heightMm));
    setCopiesInput(String(config.copies || 1));
  }, [config.widthMm, config.heightMm, config.copies, config.preset]);

  // Real-time direct high-precision label preview rendering to clean dataUrl
  useEffect(() => {
    let active = true;
    setIsRendering(true);
    renderLabelToCanvas(ticket, companyInfo, config)
      .then(srcCanvas => {
        if (!active) return;
        try {
          const url = srcCanvas.toDataURL('image/png');
          setPreviewDataUrl(url);
        } catch (e) {
          console.error('Failed to export preview data URL:', e);
        }
        setIsRendering(false);
      })
      .catch(err => {
        console.error('Error rendering label preview:', err);
        if (active) setIsRendering(false);
      });

    return () => {
      active = false;
    };
  }, [ticket, companyInfo, config]);

  // Handle Preset Select
  const handlePresetSelect = (presetKey: LabelConfig['preset']) => {
    const dim = PRESET_DIMENSIONS[presetKey] || PRESET_DIMENSIONS['60x40'];
    const updated: LabelConfig = {
      ...config,
      preset: presetKey,
      widthMm: dim.widthMm,
      heightMm: dim.heightMm,
    };
    setConfig(updated);
    saveStoredLabelConfig(updated);
    showSaveIndicator();
  };

  // Update config helper with autosave
  const updateConfig = (updater: Partial<LabelConfig>) => {
    const updated: LabelConfig = { ...config, ...updater };
    setConfig(updated);
    saveStoredLabelConfig(updated);
    showSaveIndicator();
  };

  const showSaveIndicator = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 1400);
  };

  // Reset to Factory Defaults
  const handleResetDefaults = () => {
    const defaults = { ...DEFAULT_LABEL_CONFIG };
    setConfig(defaults);
    saveStoredLabelConfig(defaults);
    setWidthInput(String(defaults.widthMm));
    setHeightInput(String(defaults.heightMm));
    setCopiesInput(String(defaults.copies || 1));
    setResetSuccess(true);
    setTimeout(() => setResetSuccess(false), 2000);
  };

  // Swap Width and Height Dimensions
  const handleSwapDimensions = () => {
    const newWidth = config.heightMm;
    const newHeight = config.widthMm;
    setWidthInput(String(newWidth));
    setHeightInput(String(newHeight));
    const updated: LabelConfig = {
      ...config,
      widthMm: newWidth,
      heightMm: newHeight,
      preset: 'custom',
    };
    setConfig(updated);
    saveStoredLabelConfig(updated);
    showSaveIndicator();
  };

  // Actions
  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      await printCustomLabel(ticket, companyInfo, config);
      setTimeout(() => setIsPrinting(false), 1200);
    } catch (err) {
      console.error('Print label error:', err);
      setIsPrinting(false);
    }
  };

  const handlePDFPrint = async () => {
    try {
      setIsPrinting(true);
      await printCustomLabelPDF(ticket, companyInfo, config);
      setTimeout(() => setIsPrinting(false), 1200);
    } catch (err) {
      console.error('PDF label print error:', err);
      setIsPrinting(false);
    }
  };

  const handlePDFExport = async () => {
    try {
      setIsDownloading(true);
      await exportCustomLabelPDF(ticket, companyInfo, config);
      setTimeout(() => setIsDownloading(false), 1200);
    } catch (err) {
      console.error('PDF export error:', err);
      setIsDownloading(false);
    }
  };

  // Automatic Fit Metrics Calculation
  const m = calculateLabelMetrics(ticket, companyInfo, config);

  return (
    <div id="label-designer-container" className="flex flex-col lg:flex-row gap-4 p-4 sm:p-5 bg-slate-100/70 min-h-[540px] max-h-[82vh] h-[620px] select-none overflow-hidden">
      {/* ================= LEFT PANEL: SETTINGS & CONTROLS ================= */}
      <div className="w-full lg:w-[370px] flex flex-col gap-3 shrink-0 h-full overflow-hidden">
        {/* Header with Quick Actions & Auto-Save */}
        <div className="bg-white px-3.5 py-2.5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xs font-black text-slate-900 flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-blue-600" />
              Etiket Boyut & Tasarım
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5 font-medium">
              {config.preset !== 'custom'
                ? PRESET_DIMENSIONS[config.preset]?.label
                : `${config.widthMm} × ${config.heightMm} mm`}
            </p>
          </div>

          <div className="flex items-center gap-1.5">
            {saveSuccess && (
              <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 animate-fade-in">
                <Check className="w-3 h-3" /> Kaydedildi
              </span>
            )}
            <button
              type="button"
              onClick={handleResetDefaults}
              title="Varsayılan standart ayarlara sıfırla"
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${resetSuccess ? 'text-emerald-600 animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-slate-200/80 p-1 rounded-xl gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('size')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'size'
                ? 'bg-white text-blue-600 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span>Boyut & Düzen</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('fields')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'fields'
                ? 'bg-white text-blue-600 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Bilgi Alanları</span>
          </button>
        </div>

        {/* ================= TAB CONTENT (Scrolls internally if needed) ================= */}
        <div className="flex-1 overflow-y-auto pr-1">
          {/* ================= TAB 1: BOYUT & DÜZEN ================= */}
          {activeTab === 'size' && (
            <div className="space-y-3">
              {/* Presets Grid */}
              <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs space-y-2.5">
                <label className="text-[11px] font-bold text-slate-800 block">
                  Hazır Termal Etiket Boyutları
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {(Object.keys(PRESET_DIMENSIONS) as Array<LabelConfig['preset']>).map(presetKey => {
                    const p = PRESET_DIMENSIONS[presetKey];
                    const isSelected = config.preset === presetKey;
                    return (
                      <button
                        key={presetKey}
                        type="button"
                        onClick={() => handlePresetSelect(presetKey)}
                        className={`p-2 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer relative ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50/60 ring-2 ring-blue-500/20'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/80 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className={`text-[11px] font-black ${isSelected ? 'text-blue-700' : 'text-slate-900'}`}>
                            {p.label}
                          </span>
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-blue-600" />}
                        </div>
                        <span className="text-[9.5px] text-slate-500 line-clamp-1 mt-0.5 font-medium">
                          {p.desc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Millimeter Dimensions (Width x Height) */}
              <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-slate-800">
                    Milimetrik Ölçüler (Genişlik × Yükseklik)
                  </label>
                  <button
                    type="button"
                    onClick={handleSwapDimensions}
                    title="Genişlik ve Yükseklik değerlerinin yerini değiştir"
                    className="text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-md transition-colors flex items-center gap-1 cursor-pointer border border-blue-200/60"
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                    <span>Ölçüleri Çevir</span>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-600 block mb-0.5">
                      Genişlik (mm)
                    </span>
                    <input
                      type="number"
                      min={25}
                      max={160}
                      value={widthInput}
                      onChange={e => {
                        const val = e.target.value;
                        setWidthInput(val);
                        const num = parseInt(val, 10);
                        if (!isNaN(num) && num >= 20 && num <= 200) {
                          updateConfig({ widthMm: num, preset: 'custom' });
                        }
                      }}
                      className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:bg-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-slate-600 block mb-0.5">
                      Yükseklik (mm)
                    </span>
                    <input
                      type="number"
                      min={20}
                      max={160}
                      value={heightInput}
                      onChange={e => {
                        const val = e.target.value;
                        setHeightInput(val);
                        const num = parseInt(val, 10);
                        if (!isNaN(num) && num >= 20 && num <= 200) {
                          updateConfig({ heightMm: num, preset: 'custom' });
                        }
                      }}
                      className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:bg-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Padding & Copies */}
                <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-600 block mb-0.5">
                      Kenar Boşluğu (mm)
                    </span>
                    <div className="flex bg-slate-100 p-0.5 rounded-xl">
                      {[1, 2, 3, 4].map(pad => (
                        <button
                          key={pad}
                          type="button"
                          onClick={() => updateConfig({ paddingMm: pad })}
                          className={`flex-1 py-0.5 text-[10px] font-bold rounded-lg transition-colors cursor-pointer ${
                            (config.paddingMm || 2) === pad
                              ? 'bg-white text-blue-600 shadow-xs'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          {pad}mm
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-semibold text-slate-600 block mb-0.5">
                      Kopya Sayısı
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={copiesInput}
                      onChange={e => {
                        const val = e.target.value;
                        setCopiesInput(val);
                        const num = parseInt(val, 10);
                        if (!isNaN(num) && num >= 1 && num <= 50) {
                          updateConfig({ copies: num });
                        }
                      }}
                      className="w-full px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:bg-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Print Rotation / Thermal Roll Orientation Correction */}
                <div className="pt-2 border-t border-slate-100 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-slate-700 flex items-center gap-1">
                      <RotateCw className="w-3 h-3 text-slate-500" />
                      Yazdırma Döndürme Açısı (Termal Rulo Yönü)
                    </span>
                    <span className="text-[9.5px] text-slate-400 font-mono">
                      {config.rotation || 0}°
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 bg-slate-100 p-0.5 rounded-xl">
                    {([0, 90, 180, 270] as const).map(angle => (
                      <button
                        key={angle}
                        type="button"
                        onClick={() => updateConfig({ rotation: angle })}
                        className={`py-1 text-[10px] font-bold rounded-lg transition-colors cursor-pointer text-center ${
                          (config.rotation || 0) === angle
                            ? 'bg-white text-blue-600 shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {angle === 0 ? '0° (Düz)' : `${angle}°`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= TAB 2: BILGI ALANLARI ================= */}
          {activeTab === 'fields' && (
            <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs space-y-2.5">
              <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                <label className="text-[11px] font-bold text-slate-800">
                  Görünecek Bilgi Alanları
                </label>
                <span className="text-[9.5px] text-slate-500">Etikette yer alacakları seçin</span>
              </div>

              <div className="space-y-1.5">
                {[
                  { key: 'showCompanyName', label: 'Firma Adı & Üst Başlık' },
                  { key: 'showCompanyPhone', label: 'Firma Telefonu' },
                  { key: 'showTicketId', label: 'Servis Fiş No Rozeti' },
                  { key: 'showReceivedDate', label: 'Üst Kayıt Tarihi' },
                  { key: 'showCustomerName', label: 'Müşteri Adı Soyadı' },
                  { key: 'showCustomerPhone', label: 'Müşteri Telefonu' },
                  { key: 'showDeviceBrandModel', label: 'Cihaz Marka & Model' },
                  { key: 'showDevicePassword', label: 'Cihaz Şifresi / Kilit Rozeti' },
                  { key: 'showAccessories', label: 'Gelen Aksesuarlar' },
                  { key: 'showSerialNumber', label: 'Seri Numarası' },
                  { key: 'showWarrantyStatus', label: 'Garanti Durumu' },
                  { key: 'showIssueDescription', label: 'Arıza / Şikayet Açıklaması' },
                ].map(field => {
                  const isChecked = Boolean(config[field.key as keyof LabelConfig]);
                  return (
                    <label
                      key={field.key}
                      className="flex items-center justify-between p-1.5 px-2 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <span className="text-[11px] font-semibold text-slate-800">
                        {field.label}
                      </span>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={e =>
                          updateConfig({ [field.key]: e.target.checked } as Partial<LabelConfig>)
                        }
                        className="w-3.5 h-3.5 accent-blue-600 rounded cursor-pointer"
                      />
                    </label>
                  );
                })}

                {/* Custom Footer Note */}
                <div className="pt-2 border-t border-slate-100 space-y-1.5">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-[11px] font-bold text-slate-800">
                      Özel Dipnot / Alt Bilgi
                    </span>
                    <input
                      type="checkbox"
                      checked={config.showCustomNote}
                      onChange={e => updateConfig({ showCustomNote: e.target.checked })}
                      className="w-3.5 h-3.5 accent-blue-600 rounded cursor-pointer"
                    />
                  </label>
                  {config.showCustomNote && (
                    <input
                      type="text"
                      value={config.customNoteText || ''}
                      onChange={e => updateConfig({ customNoteText: e.target.value })}
                      placeholder="Özel etiket notu..."
                      className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-900 focus:bg-white focus:border-blue-500 focus:outline-none"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= RIGHT PANEL: INTERACTIVE LIVE PREVIEW & PRINT BAR ================= */}
      <div className="flex-1 flex flex-col items-center justify-between gap-3 h-full overflow-hidden">
        {/* Preview Top Status Bar */}
        <div className="w-full flex items-center justify-between bg-white px-3.5 py-2.5 rounded-2xl border border-slate-200 shadow-xs text-xs shrink-0">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
            <span className="font-bold text-slate-900">Etiket Önizleme</span>
            <span className="text-slate-400 font-mono text-[11px]">
              ({m.effectiveWidth}mm × {m.effectiveHeight}mm)
            </span>
          </div>
        </div>

        {/* Live Thermal Label Card (Centered & Auto-Fitted in viewport) */}
        <div
          id="label-live-preview-box"
          className="flex-1 w-full flex items-center justify-center p-4 bg-slate-200/50 rounded-2xl border border-slate-300/80 border-dashed overflow-hidden relative min-h-[220px]"
        >
          {previewDataUrl ? (
            <div className="relative max-h-full max-w-full flex items-center justify-center">
              <img
                src={previewDataUrl}
                alt="Etiket Önizleme"
                className="max-h-[380px] max-w-full object-contain rounded-xs shadow-xl border border-slate-300/80 bg-white transition-opacity duration-150"
              />
              {isRendering && (
                <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] flex items-center justify-center rounded-xs">
                  <span className="text-[10px] font-bold text-slate-600 bg-white/90 px-2 py-1 rounded shadow-xs">
                    Yenileniyor...
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-xs">
              Önizleme hazırlanıyor...
            </div>
          )}
        </div>

        {/* ================= ACTION BUTTONS BAR ================= */}
        <div className="w-full bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-2.5 shrink-0">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <span className="flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-xl text-[11px]">
              Baskı: <strong className="text-slate-900 font-bold">{config.copies || 1} Kopya</strong>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePDFExport}
              disabled={isDownloading}
              title="Önizlemedeki etiketi birebir PDF olarak indir"
              className="py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50 border border-slate-200"
            >
              <Download className="w-3.5 h-3.5 text-emerald-600" />
              <span>{isDownloading ? 'İndiriliyor...' : 'PDF İndir'}</span>
            </button>

            <button
              type="button"
              onClick={handlePDFPrint}
              disabled={isPrinting}
              title="Doğrudan kenarlıksız ve üst/alt başlıksız termal PDF akışı ile yazdır"
              className="py-2 px-3 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
            >
              <Printer className="w-3.5 h-3.5 text-amber-400" />
              <span>Termal PDF Yazdır</span>
            </button>

            <button
              type="button"
              onClick={handlePrint}
              disabled={isPrinting}
              title="Önizlemeyi doğrudan etiket yazıcısına gönder"
              className="py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-md shadow-blue-500/25 transition-all cursor-pointer disabled:opacity-50"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>{isPrinting ? 'Yazdırılıyor...' : 'Doğrudan Yazdır'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
